import { Button, Col, Row, Table, Tag, Typography } from "antd"
import React from "react"
import PageHeader from "../../../components/Elements/PageHeader"
import { FaRegHandPaper } from "react-icons/fa"
import { OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
import useHoldOrders from "../../../store/Accounting/useHoldOrders"
import { returnProperMachineTag } from "../../../components/utils/ReturnProperMachineTag"
import { ExclamationCircleFilled, EyeInvisibleOutlined } from "@ant-design/icons"
import { doc, updateDoc } from "firebase/firestore"
import { db } from "../../../firebase/config"
import InvoicePDFPreview from "../Invoices/Helper/InvoicePDFPreview"
import HandleOrderPayment from "../Invoices/Helper/HandleOrderPayment"

const { Text } = Typography

export default function HoldOrders() {
  const { loading: holdOrdersLoading, holdOrders } = useHoldOrders()

  // Machine filter
  let machineFilters =
    holdOrders &&
    holdOrders.reduce((accumulator, item) => {
      // Assuming "machine" data is under item.selectedProduct.machine
      const machineName = item.selectedProduct.machine
      const existingItem = accumulator.find((filterItem) => filterItem.value === machineName)

      if (!existingItem) {
        accumulator.push({
          text: machineName, // Assuming you have a function to get a friendly name for each machine
          value: machineName,
        })
      }

      return accumulator
    }, [])

  //client filter
  let clientFIlters =
    holdOrders &&
    holdOrders.reduce((accumulator, item) => {
      const clientName = item.client.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)

      if (existingItem) {
        existingItem.value = clientName
      } else {
        accumulator.push({
          text: clientName,
          value: clientName,
        })
      }

      return accumulator
    }, [])

  //client filter
  let productFilters =
    holdOrders &&
    holdOrders.reduce((accumulator, item) => {
      const productName = item.selectedProduct.product.productName
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)

      if (existingItem) {
        existingItem.value = productName
      } else {
        accumulator.push({
          text: productName,
          value: productName,
        })
      }

      return accumulator
    }, [])

  const handleApprove = async (record) => {
    try {
      const docRef = doc(db, "orders", record.id)
      await updateDoc(docRef, {
        orderStatus: 1,
      })
      // Optionally, you can add feedback here (e.g. notification)
    } catch (error) {
      console.error("Error updating orderStatus: ", error)
    }
  }

  const columns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "center",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      render: (value, record) => OrderAndPrefix(record),
    },

    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "center",
      filters: clientFIlters,
      onFilter: (value, record) => record.client.client.startsWith(value),
      filterSearch: true,
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "center",
      filters: machineFilters,
      onFilter: (value, record) => record.selectedProduct.machine === value,
      render: (value) => {
        return returnProperMachineTag(value)
      },
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "center",
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct.product.productName === value,
      filterSearch: true,
      render: (value, record) => {
        // Safely access changableSpace using optional chaining and logical OR
        const changableSpace = record.selectedProduct?.advanced?.changableSpace || false
        if (changableSpace) {
          return (
            <Text>
              {value} - ({record.changableSpaceData})
            </Text>
          )
        } else {
          return <Text>{value}</Text>
        }
      },
    },
    {
      title: "Sales Manager",
      dataIndex: "user",
      align: "center",
    },
    {
      title: "Manufacturer",
      dataIndex: ["manufacturer", "company"],
      align: "center",
    },
    {
      title: "Document Type",
      align: "center",
      render: (value) => {
        if (value.orderStatus === 0.5) {
          return <Tag color="#13c2c2">PROFORMA</Tag>
        } else {
          return <Tag color="#722ed1">INVOICE</Tag>
        }
      },
    },
    {
      title: "Prof. Nr.",
      dataIndex: ["proforma", "name"],
      align: "center",
      render: (value, record) => {
        if (record.orderStatus === 0.5) {
          return <Tag style={{ color: "#1677ff" }}>{value}</Tag>
        } else {
          return <Tag style={{ color: "#1677ff" }}>{record.invoice.name}</Tag>
        }
      },
    },
    {
      title: "Payment",
      width: "5%",
      align: "center",
      render: (value, record) => {
        if (record.orderStatus === 0.5) {
          return <HandleOrderPayment record={record} collectionName="proformas" />
        } else {
          return <HandleOrderPayment record={record} collectionName="invoices" />
        }
      },
    },
    {
      title: "Show",
      dataIndex: ["proforma", "proformaUrl"],
      width: "5%",
      align: "center",
      render: (value, record) => {
        if (record.orderStatus === 0.5) {
          if (value) {
            return <InvoicePDFPreview pdfUrl={value} />
          } else {
            return <EyeInvisibleOutlined style={{ color: "#8c8c8c" }} />
          }
        } else {
          if (record.invoice.invoiceUrl) {
            return <InvoicePDFPreview pdfUrl={record.invoice.invoiceUrl} />
          } else {
            return <EyeInvisibleOutlined style={{ color: "#8c8c8c" }} />
          }
        }
      },
    },
    {
      title: "Approve",
      align: "center",
      width: "10%",
      render: (value, record) => (
        <Button
          danger
          size="small"
          icon={<ExclamationCircleFilled />}
          disabled={record.orderStatus !== 0.5}
          onClick={() => handleApprove(record)}>
          Send Manually
        </Button>
      ),
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader icon={<FaRegHandPaper />} text="Orders on Hold" />
        </Col>
        <Col span={24}>
          <Table
            size="small"
            rowKey={(record) => record.id}
            columns={columns}
            dataSource={holdOrders}
            loading={holdOrdersLoading}
          />
        </Col>
      </Row>
    </div>
  )
}
